
import Vue from 'vue';
import Component from 'vue-class-component';
import _ from 'lodash';
import {
	NewUsernameValidation, newPasswordRules, confirmPasswordRules, TenDigitPhoneValidation, ConfirmValidation,
} from '@kasasa/kloans-common-ui/dist/validations';
import { postUsernameAvailability, postCreateUser } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication';
import { AxiosResponse, AxiosPromise } from 'axios';
import { PostCreateUserResponse } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication/types/Enrollment';
import EnrollmentBase from '@/login/components/EnrollmentBase/EnrollmentBase.vue';
import { getModule } from 'vuex-module-decorators';
import LoginStore from '@/login/store';
import { store } from '@/store';

getModule(LoginStore, store);

@Component({
	validations: {
		username: NewUsernameValidation.validations(async (value: string): Promise<boolean> => {
			if (!value) {
				return true;
			}
			const token: string = sessionStorage.getItem('token') || '';
			const last4Ssn: string = sessionStorage.getItem('last4Ssn') || '';
			const dateOfBirth: string = sessionStorage.getItem('dateOfBirth') || '';
			let response: AxiosResponse<boolean>;
			try {
				response = await (postUsernameAvailability({
					token,
					ssn_last_4: last4Ssn,
					date_of_birth: dateOfBirth,
					username: value,
				}) as AxiosPromise<boolean>);
				if (response.data) {
					return true;
				}
				return false;
			} catch (error) {
				return false;
			}
		}),
		password: newPasswordRules.validations,
		password1: confirmPasswordRules.validations,
		mobileNumber: TenDigitPhoneValidation.validations(),
		mobileNumber1: ConfirmValidation.validations('mobileNumber'),
	},
	components: {
		EnrollmentBase,
	},
})
export default class CreateLogin extends Vue {
	username: string = '';

	debouncedUsername: string = '';

	usernameLoading: boolean = false;

	password: string = '';

	password1: string = '';

	mobileNumber: string = '';

	mobileNumber1: string = '';

	formErrorMessage: string = '';

	sessionExpired: boolean = false;

	timeoutState: string = 'enabled';

	token: string = '';

	last4Ssn: string = '';

	dateOfBirth: string = '';

	instructions: string = 'Use any username and a strong password. For security reasons, make sure it’s not the same as your online banking information.';

	formTemplate: string = 'createLogin';

	submitBtn: boolean = true;

	debounce?: (() => void) & any;

	get usernameErrors() {
		if (this.$v.username.$pending) {
			return [];
		}
		return NewUsernameValidation.errors(this);
	}

	get passwordErrors() {
		return newPasswordRules.errors(this);
	}

	get password1Errors() {
		return confirmPasswordRules.errors(this, 'password1', 'password');
	}

	get mobileNumberErrors() {
		return TenDigitPhoneValidation.errors(this, 'mobileNumber');
	}

	get mobileNumber1Errors() {
		return ConfirmValidation.errors(this, 'mobileNumber1', 'mobileNumber');
	}

	debounceUsername() {
		if (this.debounce) {
			this.debounce.cancel();
		}

		this.debounce = _.debounce(() => {
			// vuelidate is checking async rule on model change
			// setting input model to debouncedUsername bypasses that issue
			// make sure to call model touch so other rules are called
			this.username = this.debouncedUsername;
			this.$v.username.$touch();
		}, 1000, { leading: false, trailing: true });
		this.debounce();
	}

	created() {
		this.token = sessionStorage.getItem('token') || '';
		this.last4Ssn = sessionStorage.getItem('last4Ssn') || '';
		this.dateOfBirth = sessionStorage.getItem('dateOfBirth') || '';
	}

	setKjwt(kjwt: string) {
		sessionStorage.setItem('kjwt', kjwt);
		// Timesout in 10 minutes.
		const timeout: Date = new Date();
		timeout.setMinutes(timeout.getMinutes() + 10);
		sessionStorage.setItem('enrollmentTouch', timeout.valueOf().toString());
		this.$router.push({ name: 'verifyMobileDevice' });
	}

	async submitLogin() {
		this.$v.$touch();
		if (!this.$v.$invalid) {
			try {
				const response = await postCreateUser({
					token: this.token,
					ssn_last_4: this.last4Ssn,
					date_of_birth: this.dateOfBirth,
					username: this.username.toLowerCase(),
					password: this.password,
					phone_number: Number(this.mobileNumber),
				});
				this.userCreated(response.data);
			} catch (error) {
				this.userCreatedError((error as any).response.status, (error as any).response.data.error);
			}
		}
	}

	userCreated(response: PostCreateUserResponse) {
		const userDetails = response.data;
		const { kjwt } = userDetails;
		delete (userDetails as any).kjwt;
		sessionStorage.setItem('userDetails', JSON.stringify(userDetails));
		this.setKjwt(kjwt);
	}

	userCreatedError(status: number, error: {errors: {message: string}[]}) {
		let errorMessage = 'An unknown error has occurred.';
		switch (status) {
			case 500:
			case 422:
				errorMessage = error.errors[0].message;
				break;
			default:
				break;
		}
		this.formErrorMessage = errorMessage;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	mobileInput($event: any) {
		this.mobileNumber = $event.target.unmaskedValue;
		this.$v.mobileNumber.$touch();
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	mobile1Input($event: any) {
		this.mobileNumber1 = $event.target.unmaskedValue;
		this.$v.mobileNumber1.$touch();
	}
}
